import { z } from 'zod';

// Dynamic validation schemas that use database settings
// These will be created at runtime using useSettings() hook

// Phone number validation regex (international format)
const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;

export interface CompanyValidationMessages {
    name_required?: string;
    name_max?: string;
    company_code_required?: string;
    company_type_required?: string;
    country_required?: string;
    category_invalid?: string;
    phone_invalid?: string;
    email_invalid?: string;
}

interface CompanyValidationSettings {
    countries: string[];
    companyCategories: string[];
    companyTypes: string[];
    companyPriorities: Array<{ value: string; label: string; color: string; icon: string }>;
    messages?: CompanyValidationMessages;
}

const defaultMessages: Required<CompanyValidationMessages> = {
    name_required: 'Company name is required',
    name_max: 'Company name is too long',
    company_code_required: 'Company code is required',
    company_type_required: 'Company type is required',
    country_required: 'Country is required',
    category_invalid: 'Invalid category selected',
    phone_invalid: 'Please enter a valid phone number',
    email_invalid: 'Please enter a valid email address',
};

function resolveMessages(messages?: CompanyValidationMessages): Required<CompanyValidationMessages> {
    return { ...defaultMessages, ...(messages ?? {}) };
}

export function createCompanyValidationSchema(settings: CompanyValidationSettings) {
    const m = resolveMessages(settings.messages);
    const companyCategories = settings.companyCategories as [string, ...string[]];
    const companyTypes = settings.companyTypes as [string, ...string[]];
    const companyPriorities = settings.companyPriorities.map((p) => p.value) as [string, ...string[]];
    const countries = settings.countries as [string, ...string[]];

    return z.object({
        name: z.string().min(1, m.name_required).max(255, m.name_max),
        category: z.array(z.enum(companyCategories, { error: m.category_invalid })).optional(),
        domain: z.string().nullable().optional().or(z.literal('')),
        address: z.string().nullable().optional().or(z.literal('')),
        address_lat: z.number().nullable().optional(),
        address_lng: z.number().nullable().optional(),
        address_street: z.string().nullable().optional().or(z.literal('')),
        address_house_number: z.string().nullable().optional().or(z.literal('')),
        address_postal_code: z.string().nullable().optional().or(z.literal('')),
        address_city: z.string().nullable().optional().or(z.literal('')),
        address_municipality: z.string().nullable().optional().or(z.literal('')),
        country: z.enum(countries, { error: m.country_required }),
        company_code: z.string().min(1, m.company_code_required),
        vat: z.string().nullable().optional().or(z.literal('')),
        phone: z
            .string()
            .optional()
            .or(z.literal(''))
            .refine((val) => !val || phoneRegex.test(val), m.phone_invalid),
        email: z
            .string()
            .optional()
            .or(z.literal(''))
            .refine((val) => !val || z.string().email().safeParse(val).success, m.email_invalid),
        type: z.enum(companyTypes, { error: m.company_type_required }),
        priority: z.enum(companyPriorities).optional(),
    });
}

export function createUpdateCompanyValidationSchema(settings: CompanyValidationSettings) {
    const m = resolveMessages(settings.messages);
    const companyCategories = settings.companyCategories as [string, ...string[]];
    const companyTypes = settings.companyTypes as [string, ...string[]];
    const companyPriorities = settings.companyPriorities.map((p) => p.value) as [string, ...string[]];
    const countries = settings.countries as [string, ...string[]];

    return z.object({
        name: z.string().min(1, m.name_required).max(255, m.name_max).optional(),
        category: z.array(z.enum(companyCategories, { error: m.category_invalid })).optional(),
        domain: z.string().nullable().optional().or(z.literal('')),
        address: z.string().nullable().optional().or(z.literal('')),
        address_lat: z.number().nullable().optional(),
        address_lng: z.number().nullable().optional(),
        address_street: z.string().nullable().optional().or(z.literal('')),
        address_house_number: z.string().nullable().optional().or(z.literal('')),
        address_postal_code: z.string().nullable().optional().or(z.literal('')),
        address_city: z.string().nullable().optional().or(z.literal('')),
        address_municipality: z.string().nullable().optional().or(z.literal('')),
        country: z.enum(countries, { error: m.country_required }),
        company_code: z.string().min(1, m.company_code_required).optional(),
        vat: z.string().nullable().optional().or(z.literal('')),
        phone: z
            .string()
            .optional()
            .or(z.literal(''))
            .refine((val) => !val || phoneRegex.test(val), m.phone_invalid),
        email: z
            .string()
            .optional()
            .or(z.literal(''))
            .refine((val) => !val || z.string().email().safeParse(val).success, m.email_invalid),
        type: z.enum(companyTypes, { error: m.company_type_required }),
        priority: z.enum(companyPriorities).optional(),
    });
}

// Legacy static schemas for backward compatibility (deprecated)
export const createCompanySchema = z.object({
    name: z.string().min(1, 'Company name is required').max(255, 'Company name is too long'),
    category: z.array(z.string()).optional(),
    domain: z.string().optional().or(z.literal('')),
    address: z.string().optional().or(z.literal('')),
    address_lat: z.number().nullable().optional(),
    address_lng: z.number().nullable().optional(),
    address_street: z.string().optional().or(z.literal('')),
    address_house_number: z.string().optional().or(z.literal('')),
    address_postal_code: z.string().optional().or(z.literal('')),
    address_city: z.string().optional().or(z.literal('')),
    address_municipality: z.string().optional().or(z.literal('')),
    country: z.string(),
    company_code: z.string().min(1, 'Company code is required'),
    vat: z.string().optional().or(z.literal('')),
    phone: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine((val) => {
            if (!val || val === '') return true;
            return phoneRegex.test(val);
        }, 'Please enter a valid phone number'),
    email: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine((val) => {
            if (!val || val === '') return true;
            return z.string().email().safeParse(val).success;
        }, 'Please enter a valid email address'),
    type: z.string(),
    priority: z.string().optional(),
});

export const updateCompanySchema = z.object({
    name: z.string().min(1, 'Company name is required').max(255, 'Company name is too long').optional(),
    category: z.array(z.string()).optional(),
    domain: z.string().optional().or(z.literal('')),
    address: z.string().optional().or(z.literal('')),
    address_lat: z.number().nullable().optional(),
    address_lng: z.number().nullable().optional(),
    address_street: z.string().optional().or(z.literal('')),
    address_house_number: z.string().optional().or(z.literal('')),
    address_postal_code: z.string().optional().or(z.literal('')),
    address_city: z.string().optional().or(z.literal('')),
    address_municipality: z.string().optional().or(z.literal('')),
    country: z.string(),
    company_code: z.string().min(1, 'Company code is required').optional(),
    vat: z.string().optional().or(z.literal('')),
    phone: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine((val) => {
            if (!val || val === '') return true;
            return phoneRegex.test(val);
        }, 'Please enter a valid phone number'),
    email: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine((val) => {
            if (!val || val === '') return true;
            return z.string().email().safeParse(val).success;
        }, 'Please enter a valid email address'),
    type: z.string(),
    priority: z.string().optional(),
});

export type CreateCompanyFormData = z.infer<typeof createCompanySchema>;
export type UpdateCompanyFormData = z.infer<typeof updateCompanySchema>;
